import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TableRow, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, CalendarPlus, AlertTriangle, AlertCircle, Undo2 } from "lucide-react";
import { StockItemWithBatches, StockBatch } from "@/types/catering";
import { format } from "date-fns";

interface StockItemRowProps {
  item: StockItemWithBatches;
  onEditItem: (item: StockItemWithBatches) => void;
  onDeleteItem: (itemId: string) => void;
  onAddBatch: (item: StockItemWithBatches) => void;
  onEditBatch: (item: StockItemWithBatches, batch: StockBatch) => void;
  onDeleteBatch: (batchId: string, itemName: string, batchNumber?: string | null) => void;
  onAssignToEvent: (item: StockItemWithBatches) => void;
}

export function StockItemRow({
  item,
  onEditItem,
  onDeleteItem,
  onAddBatch,
  onEditBatch,
  onDeleteBatch,
  onAssignToEvent
}: StockItemRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const isLowStock = item.low_stock_threshold > 0 && item.totalQuantity <= item.low_stock_threshold;
  const isOutOfStock = item.totalQuantity === 0;

  const getExpiringBatchCount = () => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return item.batches.filter(b => b.expiry_date && new Date(b.expiry_date) <= weekFromNow).length;
  };

  const expiringCount = getExpiringBatchCount();

  return (
    <>
      <TableRow className={isOutOfStock ? "bg-red-50 dark:bg-red-950/20" : isLowStock ? "bg-orange-50 dark:bg-orange-950/20" : ""}>
        <TableCell>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 h-6 w-6"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {item.item_name}
            {isOutOfStock && (
              <Badge variant="destructive" className="text-xs">Out of Stock</Badge>
            )}
            {isLowStock && !isOutOfStock && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Low stock: Below threshold of {item.low_stock_threshold} {item.unit_type || 'units'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {expiringCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{expiringCount} batch(es) expiring within 7 days</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </TableCell>
        <TableCell>{item.supplier || '-'}</TableCell>
        <TableCell>
          <span className={isOutOfStock ? "text-red-600 font-semibold" : isLowStock ? "text-orange-600 font-semibold" : ""}>
            {item.totalQuantity} {item.unit_type || 'units'}
          </span>
        </TableCell>
        <TableCell>
          <Badge variant="secondary">{item.batches.length} batch{item.batches.length !== 1 ? 'es' : ''}</Badge>
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => onAddBatch(item)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add batch</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onAssignToEvent(item)}
                    disabled={isOutOfStock}
                  >
                    <CalendarPlus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Assign to event</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => onEditItem(item)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit item</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => onDeleteItem(item.id)}>
                    <Trash2 className="h-4 w-4 text-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete item and all batches</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>
      
      {/* Expanded batch section */}
      {isExpanded && item.batches.length > 0 && (
        <>
          {/* Spacer row */}
          <TableRow className="h-4 bg-transparent">
            <TableCell colSpan={6} className="p-0 border-0"></TableCell>
          </TableRow>
          
          {/* Batch header */}
          <TableRow className="bg-muted/50 border-t border-muted">
            <TableCell></TableCell>
            <TableCell className="pl-8 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Batch Number
            </TableCell>
            <TableCell className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Status
            </TableCell>
            <TableCell className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Quantity
            </TableCell>
            <TableCell className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Expiry Date
            </TableCell>
            <TableCell className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">
              Actions
            </TableCell>
          </TableRow>
          
          {/* Batch rows */}
          {item.batches.map((batch) => {
            const isExpiring = batch.expiry_date && new Date(batch.expiry_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            const isBatchOutOfStock = batch.quantity === 0;
            
            return (
              <TableRow 
                key={batch.id} 
                className={`bg-muted/20 ${isBatchOutOfStock ? "opacity-50" : ""} ${isExpiring && !isBatchOutOfStock ? "bg-red-50/50 dark:bg-red-950/10" : ""}`}
              >
                <TableCell></TableCell>
                <TableCell className="pl-8">
                  <span className="text-sm flex items-center gap-1.5">
                    {batch.batch_number?.startsWith('Return:') && (
                      <Undo2 className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                    )}
                    {batch.batch_number || '(no number)'}
                  </span>
                </TableCell>
                <TableCell>
                  {isBatchOutOfStock ? (
                    <Badge variant="outline" className="text-xs text-muted-foreground">Empty</Badge>
                  ) : isExpiring ? (
                    <Badge variant="destructive" className="text-xs">Expiring</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span className={isBatchOutOfStock ? "text-red-600" : ""}>
                    {batch.quantity} {item.unit_type || 'units'}
                  </span>
                </TableCell>
                <TableCell>
                  {batch.expiry_date ? (
                    <span className={isExpiring ? "text-red-600 font-medium" : ""}>
                      {format(new Date(batch.expiry_date), 'dd/MM/yyyy')}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">No expiry</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => onEditBatch(item, batch)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Edit batch</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => onDeleteBatch(batch.id, item.item_name, batch.batch_number)}>
                            <Trash2 className="h-4 w-4 text-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Delete batch</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          
          {/* Bottom spacer for batch section */}
          <TableRow className="bg-muted/20 h-2 border-b-2 border-muted">
            <TableCell colSpan={6} className="p-0"></TableCell>
          </TableRow>
        </>
      )}
      
      {isExpanded && item.batches.length === 0 && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
            No batches yet. <Button variant="link" className="p-0 h-auto" onClick={() => onAddBatch(item)}>Add a batch</Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
